import {
	RISK_PROFILE_REMINDER_STATUS_KEY,
	useRiskProfileReminderStatus,
	useSnoozeRiskProfileReminder,
} from "api/riskProfileReminder";
import { routesEnum } from "pages/Routes";
import { useAuthPermissions } from "hooks/useAuthUser";
import { FC, useCallback, useState } from "react";
import { useQueryCache } from "react-query";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { PERMISSIONS } from "utils/constant";
import RiskProfileReminderPopup from "./RiskProfileReminderPopup";

const RiskProfileReminderPopupContainer: FC = () => {
	const history = useHistory();
	const queryCache = useQueryCache();

	// This component only renders inside InvestorLayout (type === TYPE_LOGIN.investor is guaranteed).
	// Read permissions from localStorage — same pattern as Routes.tsx and Individual/RiskProfile.
	const permissions = useAuthPermissions();

	// Omnibus investors are excluded — reminder is only for individual and corporate investors.
	const isOmnibus = permissions.includes(PERMISSIONS.WEB_OMNIBUS_AUTHORIZE);
	const isCorporate = permissions.includes(PERMISSIONS.WEB_INVESTOR_CORP_MANAGE);
	const hasPermission = permissions.length > 0;

	// Hides the popup for the current session once the user has acted on it.
	const [dismissed, setDismissed] = useState(false);

	const { data, isLoading } = useRiskProfileReminderStatus({
		enabled: hasPermission && !isOmnibus,
		refetchOnWindowFocus: false,
	});

	const [snooze, { status: snoozeStatus }] = useSnoozeRiskProfileReminder({
		onSuccess: () => {
			setDismissed(true);
			queryCache.invalidateQueries([RISK_PROFILE_REMINDER_STATUS_KEY]);
		},
		onError: () => {
			toast.error("Something went wrong. Please try again.");
		},
	});
	const isSubmitting = snoozeStatus === "loading";

	const open = !isOmnibus && !isLoading && !dismissed && data?.shouldShowReminder === true;

	// "Yes, I'll do it now" — redirect to the Risk Profile Questionnaire flow.
	const handleConfirm = useCallback(() => {
		setDismissed(true);
		history.push(
			isCorporate ? routesEnum.investorCoporateRiskProfile : routesEnum.investorRiskProfile,
		);
	}, [history, isCorporate]);

	// "Remind me later" — snooze for 24h, then close. Guarded against duplicate submits.
	const handleSnooze = useCallback(() => {
		if (isSubmitting) return;
		snooze();
	}, [isSubmitting, snooze]);

	// Close icon (X) — per SRS this does NOT snooze; it only hides for the current
	// session and the popup will be shown again on the next login.
	const handleClose = useCallback(() => {
		if (isSubmitting) return;
		setDismissed(true);
	}, [isSubmitting]);

	return (
		<RiskProfileReminderPopup
			open={open}
			isSubmitting={isSubmitting}
			onConfirm={handleConfirm}
			onSnooze={handleSnooze}
			onClose={handleClose}
		/>
	);
};

export default RiskProfileReminderPopupContainer;
